import React, {useEffect} from "react"
import {createRoot} from "react-dom/client"
import {Provider, useDispatch, useSelector} from "react-redux"
import {Route, Router, Switch} from "react-router-dom"
import {AuthWrap} from "scholastic-client-components/src/features/AuthWrap/AuthWrap"
import {BottomRightCornerBox} from "scholastic-client-components/src/features/BottomRightCornerBox/BottomRightCornerBox"
import {MobileProvider} from "scholastic-client-components/src/features/MobileContextComponent/MobileContextComponent"
import {SocketWrapper} from "scholastic-client-components/src/features/SocketWrapper/SocketWrapperLazy"
import {useLogout} from "scholastic-client-components/src/hooks/useLogout"
import {useUpdateMessage} from "scholastic-client-components/src/hooks/useUpdateMessage"
import {configureAxios} from "scholastic-client-components/src/lib/axios"
import {BugsnagErrorBoundaryWrapper} from "scholastic-client-components/src/middlewares/bugsnagErrorBoundaryMiddleware"
import {authSelectors} from "scholastic-client-components/src/slices/authSlice"
import {APIStatus} from "scholastic-client-components/src/types/APITypes"
import {Portal} from "scholastic-client-components/src/types/securityTypes"
import {useAuth} from "scholastic-client-components/src/hooks/useAuth"
import ScholasticLoader from "scholastic-client-components/src/ui/ScholasticLoader/ScholasticLoader"
import {authAPI} from "./api/connectedAPI"
import {Notifications} from "./features/Notifications/Notifications"
import {AppPaths, history} from "./lib/routing"
import {store} from "./store/store"
import "scholastic-client-components/src/i18n/i18n"
import {I18nWrapper} from "scholastic-client-components/src/i18n/I18nWrapper"
import {SSOStatePage} from "./pages/SSOStatePage"
import {SSOState} from "./pages/Profile/SSOBC"
import {App} from "./App"

const AppSocketConnected: React.FC = () => {
    return (
        <SocketWrapper hooks={{useSelector, useDispatch}}>
            <App />
        </SocketWrapper>
    )
}

const ns = ["notifications", "home_portal", "person"]

const AppWrapper: React.FC = () => {
    const dispatch = useDispatch()

    const logout = useLogout({api: authAPI, useDispatch})
    const accessAllowed = useSelector(authSelectors.getAccessAllowed)

    useEffect(() => {
        configureAxios({store, history, portal: Portal.Home, logout})
    }, [logout])

    const auth = useAuth(Portal.Home, dispatch, useSelector, authAPI)

    useUpdateMessage(dispatch, "/home/update.html")

    return (
        <AuthWrap {...auth}>
            <I18nWrapper ns={ns}>
                <MobileProvider>
                    {accessAllowed !== undefined && (
                        <Route path="/" component={AppSocketConnected} />
                    )}
                    {accessAllowed === undefined && auth.status === APIStatus.Success && (
                        <div style={{marginTop: "10rem"}}>
                            <ScholasticLoader />
                        </div>
                    )}
                    <BottomRightCornerBox notificationNode={<Notifications />}/>
                </MobileProvider>
            </I18nWrapper>
        </AuthWrap>
    )
}

const container = document.getElementById("scholastic-root")
const root = createRoot(container!)

const render = () => {
    root.render(
        <BugsnagErrorBoundaryWrapper>
            <Provider store={store}>
                <Router history={history}>
                    <Switch>
                        <Route path={AppPaths.SSOSuccess}>
                            <SSOStatePage state={SSOState.SUCCESS} />
                        </Route>
                        <Route path={AppPaths.SSOFailure}>
                            <SSOStatePage state={SSOState.FAILURE} />
                        </Route>
                        <Route path={"/"}>
                            <AppWrapper />
                        </Route>
                    </Switch>
                </Router>
            </Provider>
        </BugsnagErrorBoundaryWrapper>
    )
}

render()
